'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ArrowRight } from '@phosphor-icons/react';
import { HomepageDemo } from '@/components/homepage-demo';
import { useModalA11y } from '@/lib/use-modal-a11y';

const STORAGE_KEY = 'galexia.first-visit-demo.seen';
const SHOW_DELAY_MS = 800;

interface FirstVisitDemoModalProps {
  /**
   * Called when the user clicks a demo (or "Try this") so the parent can
   * drop the prompt into the input. The modal closes itself first.
   */
  onPrefill: (prompt: string) => void;
}

/**
 * First-visit modal on app.galexia.ai. Shows new visitors what Galexia
 * produces in 3 seconds. localStorage flag means it fires once per browser.
 * After dismissal, the user still sees the inline HomepageDemo carousel as
 * a passive showcase on the empty state.
 */
export function FirstVisitDemoModal({ onPrefill }: FirstVisitDemoModalProps) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* localStorage may be blocked — silently ignore */
    }
  };

  useModalA11y(open, {
    dialogRef,
    primaryRef: primaryBtnRef,
    onDismiss: dismiss,
  });

  const handlePrefill = (prompt: string) => {
    onPrefill(prompt);
    dismiss();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={dismiss}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 backdrop-blur-md"
          aria-modal="true"
          role="dialog"
          aria-label="Welcome to Galexia"
        >
          <motion.div
            key="card"
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#0B0B14]"
            style={{
              boxShadow:
                '0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.6), 0 0 100px rgba(139,92,246,0.12)',
            }}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-2xl"
              style={{
                background:
                  'radial-gradient(ellipse at 30% 0%, rgba(139,92,246,0.18) 0%, transparent 60%), radial-gradient(ellipse at 70% 100%, rgba(6,182,212,0.12) 0%, transparent 60%)',
              }}
            />

            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 inline-flex size-8 items-center justify-center rounded-full border border-white/10 bg-black/50 text-white/70 backdrop-blur-sm transition-all hover:border-white/20 hover:text-white"
            >
              <X weight="bold" className="size-4" />
            </button>

            {/* Demo body — reuses the same carousel from the inline empty state */}
            <div className="relative">
              <HomepageDemo
                onPrefill={handlePrefill}
                className="!max-w-none !rounded-none !border-0 !shadow-none"
              />
            </div>

            <div className="relative px-5 py-5 sm:px-7 sm:py-6">
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-[3px] text-violet-300/70">
                    Welcome to Galexia
                  </p>
                  <h2
                    className="mt-1 font-normal leading-tight tracking-tight text-white"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24 }}
                  >
                    One workspace.{' '}
                    <span
                      className="italic"
                      style={{
                        background: 'linear-gradient(135deg, #6B8AFF, #A78BFA, #38BDF8)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Get the work done.
                    </span>
                  </h2>
                  <p className="mt-1.5 max-w-md text-[13px] text-white/55">
                    Click any preview above to drop the prompt into the input, then ask Galexia to research, build, analyze, or create.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={dismiss}
                    className="text-xs font-medium text-white/55 transition-colors hover:text-white/85"
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    ref={primaryBtnRef}
                    onClick={dismiss}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-4 py-2.5 text-xs font-semibold text-white shadow-[0_4px_24px_rgba(59,91,217,0.35)] transition-all hover:scale-[1.02] hover:shadow-[0_4px_32px_rgba(59,91,217,0.5)]"
                  >
                    Try it now
                    <ArrowRight weight="bold" className="size-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
